import { rewriteEntityTerms } from '@/lib/terminology/entity-terms'

import {
  readApiErrorMessage as extractMessageFromData,
  readApiErrorStatus as extractStatusFromData,
  parseApiErrorText,
} from '@scrambl/shared/api-errors'

export { parseApiErrorText } from '@scrambl/shared/api-errors'

type ErrorData = Record<string, unknown>

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback

  if (typeof error === 'string') {
    return parseApiErrorText(error, fallback).message
  }

  const errorObj = error as {
    message?: unknown
    data?: unknown
    response?: { data?: unknown }
  }

  const dataMessage = extractMessageFromData(
    errorObj.data ?? errorObj.response?.data,
  )
  if (dataMessage) return dataMessage

  if (typeof errorObj.message === 'string') {
    return parseApiErrorText(errorObj.message, fallback).message
  }

  const objectMessage = extractMessageFromData(errorObj)
  return objectMessage ?? fallback
}

// API messages say "entity"; users see the org's configured term
export const getErrorMessage = (
  error: unknown,
  fallback = 'An error occurred',
): string => rewriteEntityTerms(resolveErrorMessage(error, fallback))

export const getErrorStatus = (error: unknown): number | undefined => {
  if (!error) return undefined

  if (typeof error === 'object') {
    const errorObj = error as {
      status?: unknown
      statusCode?: unknown
      response?: { status?: unknown; data?: unknown }
      data?: unknown
    }

    const directStatus =
      errorObj.status ?? errorObj.statusCode ?? errorObj.response?.status
    const validatedStatus = extractStatusFromData({ status: directStatus })
    if (validatedStatus !== undefined) return validatedStatus

    const dataStatus = extractStatusFromData(
      errorObj.data ?? errorObj.response?.data,
    )
    if (typeof dataStatus === 'number') {
      return dataStatus
    }
  }

  const message =
    typeof error === 'string' ? error : (error as Error | undefined)?.message
  if (typeof message !== 'string') return undefined

  const match = message.match(/API Error\s+(\d+)/i)
  if (match) {
    const status = Number(match[1])
    return extractStatusFromData({ status })
  }

  return undefined
}

export const isAuthenticationAvailabilityError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data
  if (!data || typeof data !== 'object') return false

  const payload = data as ErrorData
  return (
    payload.code === 'AUTH_PROVIDER_UNAVAILABLE' ||
    payload.reason === 'transient'
  )
}

export const isAICreditsExhaustedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  const errorObj = error as {
    code?: unknown
    data?: unknown
    response?: { data?: unknown }
  }
  const data = errorObj.data ?? errorObj.response?.data ?? errorObj
  return (
    !!data &&
    typeof data === 'object' &&
    (data as ErrorData).code === 'AI_CREDITS_EXHAUSTED'
  )
}
